import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/common/EmptyState';
import { SkeletonCard } from '@/components/common/SkeletonCard';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { listNotifications, markAllNotificationsRead, markNotificationRead } from '@/services/authClient';
import { useNavigate } from 'react-router-dom';

export default function Notifications() {
  const [notifications, setNotifications] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    listNotifications()
      .then((data) => {
        setNotifications(data.results || []);
        setUnreadCount(data.unread_count || 0);
      })
      .catch(() => setNotifications([]));
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast('All notifications marked as read');
    } catch {
      toast.error('Could not mark all as read');
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.is_read) {
      try {
        await markNotificationRead(n.id);
        setNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, is_read: true } : item)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {}
    }
    if (n.url) navigate(n.url);
  };

  if (notifications === null) {
    return (
      <div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Notifications</h1>
        <p className="text-sm text-slate-600">Stay updated on your saved searches and favorites.</p>
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-600">Stay updated on your saved searches and favorites.</p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={handleMarkAllRead} variant="outline" size="sm" className="rounded-xl">
            Mark all read
          </Button>
        )}
      </div>
      <div className="mt-6">
        {notifications.length === 0 ? (
          <EmptyState
            icon={Bell}
            title="No notifications yet"
            description="You'll see updates here when new listings match your saved searches."
            actionLabel="Explore homes"
            onAction={() => (window.location.href = '/listings')}
          />
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={cn(
                  'flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm cursor-pointer hover:bg-slate-50 transition-colors',
                  !n.is_read && 'bg-blue-50 border-blue-100'
                )}
                data-testid="notification-item"
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-blue-100 text-blue-600">
                  <Bell size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900">{n.title}</p>
                  <p className="mt-0.5 text-sm text-slate-600">{n.body}</p>
                  <span className="mt-1 inline-block text-xs text-slate-400">
                    {new Date(n.created_at).toLocaleDateString()} at {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                {!n.is_read && <div className="mt-2 h-2 w-2 rounded-full bg-blue-600 shrink-0" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
